import React, { useEffect, useState, useCallback, useMemo, useContext } from 'react';
import {
  Loader2, ChevronLeft, ChevronRight, Filter, CalendarDays, RefreshCw,
} from 'lucide-react';

import { useSearchParams } from 'react-router-dom';

import { Button } from '../../ui-v2';
import { UsuarioContext } from '../../../providers/personaContext';
import {
  getCalendario, getCampanaById,
  getBloqueosTecnico, crearBloqueosBatch, eliminarBloqueoTecnico,
} from '../api';

import GridMensual from '../calendario/GridMensual';
import PanelDia from '../calendario/PanelDia';
import PanelFranjasDelDia from '../calendario/PanelFranjasDelDia';
import BloquearFranjaModal from '../calendario/BloquearFranjaModal';
import BloquearBulkModal from '../calendario/BloquearBulkModal';

/**
 * Calendario — vista cronológica de citas del módulo.
 *
 * Layout:
 *   - Mini-header sticky con navegación de mes + botón Hoy
 *   - Filtros pill: campaña, técnico, estado
 *   - Grid mensual (izq, 2/3) + Panel del día (der, 1/3) en lg+
 *   - En pantallas estrechas: panel del día abajo del grid
 *
 * Ruta: /citas-tecnicas/calendario
 * Permiso: intervenciones.campanas.view
 */

const MESES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

const ESTADOS_FILTRO = [
  { id: '', label: 'Todos los estados' },
  { id: 'reservada,confirmada', label: 'Vivas' },
  { id: 'reservada', label: 'Reservadas' },
  { id: 'confirmada', label: 'Confirmadas' },
  { id: 'realizada', label: 'Realizadas' },
  { id: 'no_asistio', label: 'No asistió' },
  { id: 'cancelada_usuario,cancelada_sistemas', label: 'Canceladas' },
];

export default function Calendario() {
  const hoy = useMemo(() => new Date(), []);
  const [mesRef, setMesRef] = useState(() => ({
    year: hoy.getFullYear(),
    month: hoy.getMonth() + 1,
  }));
  const [diaSel, setDiaSel] = useState(() => hoy.toISOString().slice(0, 10));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filtroCampana, setFiltroCampana] = useState('');
  const [filtroTecnico, setFiltroTecnico] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');

  // Deep-link `?campana_id=X` (enlace desde el wizard de edición, Fase 12i):
  // aplica el filtro de campaña SOLO al montar; no crea binding continuo.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const cid = searchParams.get('campana_id');
    if (cid) setFiltroCampana(String(parseInt(cid, 10)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detalle de la campaña filtrada (Fase 12c): aporta duracion_cita + franjas
  // para el panel lateral de franjas. Solo cuando hay una campaña seleccionada.
  const [campanaSel, setCampanaSel] = useState(null);
  useEffect(() => {
    if (!filtroCampana) { setCampanaSel(null); return; }
    let cancelado = false;
    getCampanaById(filtroCampana)
      .then((c) => { if (!cancelado) setCampanaSel(c); })
      .catch(() => { if (!cancelado) setCampanaSel(null); });
    return () => { cancelado = true; };
  }, [filtroCampana]);

  // Bloqueos del técnico (Fase 12d). Admin = puede bloquear a cualquier técnico.
  const { persona, tienePermiso } = useContext(UsuarioContext);
  const esAdmin = persona?.rol === 'Super Administrador'
    || (typeof tienePermiso === 'function' && tienePermiso('intervenciones.campanas.manage'));
  const [bloqueos, setBloqueos] = useState([]);
  const [modalBloqueo, setModalBloqueo] = useState({ open: false, modo: null, ctx: null });
  // Modal de bloqueo en bloque (Fase 12h): "todo el día" o "N slots seleccionados".
  const [modalBulk, setModalBulk] = useState({ open: false, titulo: '', rangos: [], persona_id: null });

  // Rango: primer día del mes a último día (con margen para mostrar inicio/fin de semanas adyacentes)
  const rangoApi = useMemo(() => {
    const inicio = new Date(Date.UTC(mesRef.year, mesRef.month - 1, 1));
    // Ampliar a la semana completa visible en el grid
    const inicioGrid = new Date(inicio);
    const dowIni = (inicio.getUTCDay() + 6) % 7;
    inicioGrid.setUTCDate(inicio.getUTCDate() - dowIni);
    const finGrid = new Date(inicioGrid);
    finGrid.setUTCDate(finGrid.getUTCDate() + 41);
    return {
      desde: inicioGrid.toISOString().slice(0, 10),
      hasta: finGrid.toISOString().slice(0, 10),
    };
  }, [mesRef]);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { ...rangoApi };
      if (filtroCampana) params.campana_id = filtroCampana;
      if (filtroTecnico) params.tecnico_id = filtroTecnico;
      if (filtroEstado) params.estado = filtroEstado;
      const res = await getCalendario(params);
      setData(res);
    } catch (e) {
      console.error('[Calendario]', e);
      setError(e?.response?.data?.error || 'No se pudo cargar el calendario.');
    } finally {
      setLoading(false);
    }
  }, [rangoApi, filtroCampana, filtroTecnico, filtroEstado]);

  useEffect(() => { cargar(); }, [cargar]);

  function navegarMes(delta) {
    setMesRef((m) => {
      let mm = m.month + delta, yy = m.year;
      if (mm < 1) { mm = 12; yy--; }
      if (mm > 12) { mm = 1; yy++; }
      return { year: yy, month: mm };
    });
  }

  function irHoy() {
    const ahora = new Date();
    setMesRef({ year: ahora.getFullYear(), month: ahora.getMonth() + 1 });
    setDiaSel(ahora.toISOString().slice(0, 10));
  }

  const citasDia = useMemo(() => {
    if (!data || !diaSel) return [];
    return data.citas.filter((c) => c.fecha === diaSel);
  }, [data, diaSel]);

  // Citas VIVAS del día (para el panel de franjas: ocupación real, sin canceladas).
  // Aplana el nombre del usuario a `persona_nombre` (lo que espera el panel).
  const citasDiaOcupacion = useMemo(
    () => citasDia
      .filter((c) => c.estado === 'reservada' || c.estado === 'confirmada')
      .map((c) => ({ ...c, persona_nombre: c.usuario?.nombre_completo })),
    [citasDia]
  );

  // Bloqueos del técnico filtrado, ya expandidos por día por el backend.
  const cargarBloqueos = useCallback(async () => {
    if (!filtroTecnico || !diaSel) { setBloqueos([]); return; }
    try {
      const data = await getBloqueosTecnico(filtroTecnico, { desde: diaSel, hasta: diaSel });
      setBloqueos(data);
    } catch (e) {
      setBloqueos([]);
    }
  }, [filtroTecnico, diaSel]);
  useEffect(() => { cargarBloqueos(); }, [cargarBloqueos]);

  async function handleGuardarBloqueos(bloqueosArr) {
    await crearBloqueosBatch(bloqueosArr);
    await cargarBloqueos();
  }
  async function handleDesbloquear(bloqueo) {
    if (!window.confirm('¿Desbloquear esta franja/slot?')) return;
    await eliminarBloqueoTecnico(bloqueo.id);
    setBloqueos((prev) => prev.filter((b) => b.id !== bloqueo.id));
  }

  // Bloqueo en bloque (Fase 12h): abre el modal bulk con los rangos ya fijados.
  function handleBloquearDia({ franjas, fecha }) {
    setModalBulk({
      open: true,
      titulo: 'Bloquear día completo',
      rangos: franjas,               // [{hora_inicio, hora_fin}, ...]
      persona_id: filtroTecnico || null,
    });
  }
  function handleBloquearSeleccion({ slots }) {
    setModalBulk({
      open: true,
      titulo: `Bloquear ${slots.length} ${slots.length === 1 ? 'slot' : 'slots'}`,
      rangos: slots.map((s) => ({ hora_inicio: s.hora_inicio, hora_fin: s.hora_fin })),
      persona_id: filtroTecnico || null,
    });
  }

  return (
    <div className="min-h-full">
      {/* MINI-HEADER STICKY */}
      <div className="sticky top-14 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <CalendarDays className="w-5 h-5 text-slate-400 shrink-0" />
            <h1 className="text-sm font-medium text-slate-900">Calendario</h1>

            <div className="flex items-center gap-1 ml-3">
              <button
                type="button"
                onClick={() => navegarMes(-1)}
                className="w-8 h-8 grid place-items-center rounded-md hover:bg-slate-100"
                aria-label="Mes anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium text-slate-700 min-w-[140px] text-center tabular-nums">
                {MESES[mesRef.month - 1]} {mesRef.year}
              </span>
              <button
                type="button"
                onClick={() => navegarMes(1)}
                className="w-8 h-8 grid place-items-center rounded-md hover:bg-slate-100"
                aria-label="Mes siguiente"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <Button variant="outline" size="sm" onClick={irHoy}>
              Hoy
            </Button>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={cargar}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            Actualizar
          </Button>
        </div>
      </div>

      {/* CONTENIDO */}
      <div className="px-6 py-6">
        {/* Filtros */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Filter className="w-3.5 h-3.5" />
            Filtrar
          </div>
          <select
            value={filtroCampana}
            onChange={(e) => setFiltroCampana(e.target.value)}
            className="border border-slate-200 rounded-md px-2.5 py-1.5 text-xs bg-white"
          >
            <option value="">Todas las campañas</option>
            {(data?.catalogos?.campanas || []).map((c) => (
              <option key={c.id} value={c.id}>{c.titulo}</option>
            ))}
          </select>
          <select
            value={filtroTecnico}
            onChange={(e) => setFiltroTecnico(e.target.value)}
            className="border border-slate-200 rounded-md px-2.5 py-1.5 text-xs bg-white"
          >
            <option value="">Todos los técnicos</option>
            {(data?.catalogos?.tecnicos || []).map((t) => (
              <option key={t.id} value={t.id}>{t.nombre_completo}</option>
            ))}
          </select>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="border border-slate-200 rounded-md px-2.5 py-1.5 text-xs bg-white"
          >
            {ESTADOS_FILTRO.map((e) => (
              <option key={e.id} value={e.id}>{e.label}</option>
            ))}
          </select>
          {data && (
            <span className="text-xs text-slate-500 ml-auto">
              {data.total} {data.total === 1 ? 'cita en este mes' : 'citas en este mes'}
            </span>
          )}
        </div>

        {error && (
          <div className="mb-4 border border-red-200 bg-red-50 rounded-lg p-3 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Grid + Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          <div className="lg:col-span-2">
            {loading && !data ? (
              <div className="py-16 text-center text-sm text-slate-500">
                <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
                Cargando…
              </div>
            ) : (
              <GridMensual
                year={mesRef.year}
                month={mesRef.month}
                citas={data?.citas || []}
                diaSeleccionado={diaSel}
                onSeleccionar={setDiaSel}
              />
            )}
          </div>

          <div className="lg:col-span-1 space-y-4">
            <PanelDia fecha={diaSel} citas={citasDia} />
            {filtroCampana && campanaSel && (
              <PanelFranjasDelDia
                campanaId={Number(filtroCampana)}
                fechaSeleccionada={diaSel}
                duracionCita={campanaSel.duracion_cita}
                franjasPorDia={campanaSel.franjas || {}}
                citasDelDia={citasDiaOcupacion}
                bloqueosDelDia={filtroTecnico ? bloqueos : null}
                onBloquearFranja={({ franja, fecha }) => setModalBloqueo({
                  open: true, modo: 'franja',
                  ctx: { fecha, hora_inicio: franja.hora_inicio, hora_fin: franja.hora_fin },
                })}
                onBloquearSlot={({ slot, fecha }) => setModalBloqueo({
                  open: true, modo: 'slot',
                  ctx: { fecha, hora_inicio: slot.hora_inicio, hora_fin: slot.hora_fin },
                })}
                onDesbloquear={handleDesbloquear}
                onBloquearDia={handleBloquearDia}
                onBloquearSeleccion={handleBloquearSeleccion}
              />
            )}
          </div>
        </div>

        {/* Leyenda */}
        <div className="mt-6 flex items-center gap-4 flex-wrap text-[11px] text-slate-500">
          <span className="font-medium">Leyenda:</span>
          <LegendItem color="#10b981" label="Reservada / Confirmada" />
          <LegendItem color="#003c66" label="Realizada" />
          <LegendItem color="#f59e0b" label="No asistió" />
          <LegendItem color="#cbd5e1" label="Cancelada" />
        </div>
      </div>

      {modalBloqueo.open && (
        <BloquearFranjaModal
          open={modalBloqueo.open}
          modo={modalBloqueo.modo}
          fecha={modalBloqueo.ctx.fecha}
          horaInicio={modalBloqueo.ctx.hora_inicio}
          horaFin={modalBloqueo.ctx.hora_fin}
          esAdmin={esAdmin}
          tecnicos={campanaSel?.tecnicos || []}
          tecnicoIdActual={filtroTecnico ? Number(filtroTecnico) : null}
          fechaLimiteCampana={campanaSel?.fecha_limite ? String(campanaSel.fecha_limite).slice(0, 10) : undefined}
          onSave={handleGuardarBloqueos}
          onClose={() => setModalBloqueo({ open: false, modo: null, ctx: null })}
        />
      )}

      {modalBulk.open && (
        <BloquearBulkModal
          open={modalBulk.open}
          titulo={modalBulk.titulo}
          fecha={diaSel}
          rangos={modalBulk.rangos}
          esAdmin={esAdmin}
          tecnicos={campanaSel?.tecnicos || []}
          tecnicoIdActual={modalBulk.persona_id}
          fechaLimiteCampana={campanaSel?.fecha_limite ? String(campanaSel.fecha_limite).slice(0, 10) : undefined}
          onSave={handleGuardarBloqueos}
          onClose={() => setModalBulk({ open: false, titulo: '', rangos: [], persona_id: null })}
        />
      )}
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block w-3 h-1.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}
